import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { TreeSelect } from 'antd';

import { getIdMap } from '../../../../helpers/helpers';
import { getEligibleCategoriesForCostcode } from '../../../../helpers/costcodeHelpers';
import { getPhaseCostcodeTreeData } from '../../../formHelpers';

const filterTreeByCostcode = (nodes, filterCostcode, costcodeMap) => {
  if (!nodes?.length || !filterCostcode) return nodes;

  return nodes.flatMap((node) => {
    if (node.children?.length) {
      const filteredChildren = filterTreeByCostcode(node.children, filterCostcode, costcodeMap);
      if (!filteredChildren.length) return [];
      return [{ ...node, children: filteredChildren }];
    }

    const [, costcodeId] = node.value?.split('.') ?? [];
    if (!costcodeId) return [node];
    if (!filterCostcode(costcodeMap[costcodeId])) return [];
    return [node];
  });
};

const expandCostcodeNodesWithCategories = (
  phaseChildren,
  costcodeMap,
  categoriesIdMap,
  categoryFilter,
) => (
  phaseChildren?.flatMap((ccNode) => {
    const [, costcodeId] = ccNode.value.split('.');
    const costcode = costcodeMap[costcodeId] || {};
    const eligibleIds = categoryFilter
      ? getEligibleCategoriesForCostcode(costcode, categoriesIdMap, { filter: categoryFilter })
      : (costcode.categoryIds || []);

    if (!eligibleIds.length) return [];

    if (eligibleIds.length <= 1) {
      return [ccNode];
    }

    return [{
      value: `${ccNode.value}-categories`,
      title: ccNode.title,
      selectable: false,
      children: eligibleIds.map((categoryId) => {
        const leafValue = `${ccNode.value}.${categoryId}`;
        const categoryLabel = categoriesIdMap[categoryId]?.name;
        return {
          value: leafValue,
          title: `${ccNode.title} (${categoryLabel})`,
          isLeaf: true,
        };
      }),
    }];
  })
);

export default function CostCodeTableSelector({
  projectId,
  onCostcodeUpdated,
  value,
  entryId,
  costcodes,
  costcodeMap,
  categoryFilter,
  filterCostcode,
  globalOnly = false,
  style,
}) {
  const phases = useSelector((state) => state.costcodes.phases);
  const projects = useSelector((state) => state.projects.projects);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);

  const treeProjectId = globalOnly ? null : projectId;

  const costcodeTreeOptions = useMemo(() => {
    const treeData = getPhaseCostcodeTreeData({
      projectId: treeProjectId,
      phaseId: globalOnly ? 'Unphased' : undefined,
      phases,
      costcodes,
      costcodeMap,
      projectMap,
      isClockIn: false,
    });

    return treeData.map((phase) => ({
      ...phase,
      children: filterTreeByCostcode(
        expandCostcodeNodesWithCategories(
          phase.children,
          costcodeMap,
          categoriesIdMap,
          categoryFilter,
        ),
        filterCostcode,
        costcodeMap,
      ),
    })).filter((phase) => phase.children?.length);
  }, [
    treeProjectId,
    globalOnly,
    phases,
    costcodes,
    costcodeMap,
    projectMap,
    categoriesIdMap,
    categoryFilter,
    filterCostcode,
  ]);

  return (
    <TreeSelect
      showSearch
      allowClear
      treeDefaultExpandAll
      treeNodeFilterProp="title"
      treeData={costcodeTreeOptions}
      value={value}
      onChange={(val) => onCostcodeUpdated(entryId, val ?? '')}
      style={style}
    />
  );
}

CostCodeTableSelector.propTypes = {
  projectId: PropTypes.string,
  onCostcodeUpdated: PropTypes.func.isRequired,
  entryId: PropTypes.string.isRequired,
  value: PropTypes.string,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeMap: PropTypes.shape({}),
  categoryFilter: PropTypes.oneOf(['quantityTracking', 'equipmentAndLabor']),
  filterCostcode: PropTypes.func,
  globalOnly: PropTypes.bool,
  style: PropTypes.shape({}),
};

CostCodeTableSelector.defaultProps = {
  projectId: undefined,
  value: undefined,
  costcodes: [],
  costcodeMap: {},
  categoryFilter: undefined,
  filterCostcode: undefined,
  globalOnly: false,
  style: { width: '95%' },
};
